import { SelectChangeEvent, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import FieldRow from 'components/FormElements/FieldRow';
import { numberToDisplayString } from 'further-ui/utils';
import AppTextInput from 'components/FormElements/AppTextInput';
import { NumericFormat } from 'react-number-format';
import { PartialRealisationAllocationLogic } from 'further-types/lp/realisation';
import { AppSimpleSelect } from 'components/FormElements';
import HoldingClassActions from './HoldingClassActions';
import UnitToInvestmentAllocationTable from './UnitToInvestmentAllocationTable';
import useHoldingClassFields from './hooks/useHoldingClassFields';
import useUnitAllocation from './hooks/useUnitAllocation';
import { type InvestmentHistory } from '../types';
import { PARTIAL_REALISATION_ALLOCATION_OPTIONS } from './constants';

type HoldingClassRealisationProps = {
  index: number;
  holdingClassOptions: Array<{
    value: string;
    label: string;
  }>;
  control: Control<rules.CreateRealisationFormValues>;
  holdingClassTotals: Array<{
    holdingClassId: string;
    currentNumberOfUnits: number;
  }>;
  investmentHistory: InvestmentHistory;
  onAddHoldingClass: () => void;
  onRemoveHoldingClass: () => void;
  clearConsiderationForm: () => void;
  isFirst: boolean;
  isLast: boolean;
};

const HoldingClassRealisation: React.FC<HoldingClassRealisationProps> = ({
  index,
  holdingClassOptions,
  control,
  holdingClassTotals,
  investmentHistory,
  onAddHoldingClass,
  onRemoveHoldingClass,
  clearConsiderationForm,
  isFirst,
  isLast,
}: HoldingClassRealisationProps) => {
  const { watch, setError, clearErrors, setValue, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();

  const {
    unitsSoldFieldName,
    selectedHoldingClassId,
    selectedPartialRealisationAllocationLogic,
    unitsSold,
    selectedPartialRealisationAllocationLogicFieldName,
  } = useHoldingClassFields({ index, watch });

  const holdingClassTotal = holdingClassTotals.find(
    (total) => total.holdingClassId === selectedHoldingClassId,
  ) ?? {
    holdingClassId: '',
    currentNumberOfUnits: 0,
  };

  const investmentsWithUnits = investmentHistory.filter(
    (investment) => investment.currentNumberOfUnits > 0,
  );

  const { onManualAllocationChange, unitsAllocationPerInvestment } =
    useUnitAllocation({
      index,
      unitsSold: unitsSold ?? 0,
      selectedPartialRealisationAllocationLogic,
      holdingClassTotal,
      investmentHistory: investmentsWithUnits,
      setValue,
      getValues,
      selectedHoldingClassId,
    });

  const handleUnitsSoldValueChange = (
    values: { floatValue?: number },
    onChange: (value: number | undefined) => void,
  ) => {
    const numValue = values.floatValue ?? 0;
    onChange(numValue);

    if (numValue && numValue > holdingClassTotal.currentNumberOfUnits) {
      setError(unitsSoldFieldName, {
        type: 'validate',
        message: `The maximum number of units available for sale are ${holdingClassTotal.currentNumberOfUnits}. Please change the number to be sold and try again.`,
      });
    } else {
      clearErrors(unitsSoldFieldName);
    }
  };

  const handleUnitsSoldBlur = () => {
    if (unitsSold && unitsSold > 0) {
      clearConsiderationForm();
    }
  };

  const isTotalRealisation =
    !!unitsSold && holdingClassTotal.currentNumberOfUnits === unitsSold;

  const hasMultipleInvestments = investmentHistory.length > 1;

  const shouldShowPartialRealisationAllocationLogic =
    selectedHoldingClassId && unitsSold && hasMultipleInvestments;

  return (
    <>
      <FieldRow title="Holding class" centerTitle>
        <Controller
          name={`holdingClassRealisation.${index}.holdingClassId`}
          control={control}
          render={({
            field: { ref, onChange: fieldOnChange, ...rest },
            fieldState: { error },
          }) => (
            <AppSimpleSelect
              {...rest}
              fullWidth
              options={holdingClassOptions}
              disabled={holdingClassOptions.length === 0}
              placeholder="Select existing holding class"
              defaultValue={''}
              error={!!error}
              errorMessage={error?.message}
              ref={ref}
              key={`holding-class-select-${index}-${holdingClassOptions.length}`}
              onChange={(event: SelectChangeEvent<unknown>) => {
                fieldOnChange(event.target.value);
                setValue(unitsSoldFieldName, undefined);
                setValue(
                  selectedPartialRealisationAllocationLogicFieldName,
                  PartialRealisationAllocationLogic.ProRata,
                );
              }}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Total units held" centerTitle>
        <Typography variant="body2">
          {numberToDisplayString(holdingClassTotal?.currentNumberOfUnits ?? 0)}
        </Typography>
      </FieldRow>
      <Controller
        name={`holdingClassRealisation.${index}.unitsSold`}
        control={control}
        render={({
          field: { value, onChange, ...restField },
          fieldState: { error },
        }) => (
          <FieldRow
            title="Units sold"
            tooltipText="Add an estimated number of units if the actual number of units is unknown at the point of processing. This transaction can be edited after submission to include the actual number of units."
            centerTitle
          >
            <NumericFormat
              {...restField}
              value={value ?? ''}
              onValueChange={(values) => {
                handleUnitsSoldValueChange(values, onChange);
              }}
              decimalScale={5}
              allowLeadingZeros={false}
              thousandSeparator={true}
              customInput={AppTextInput}
              error={!!error}
              helperText={error?.message}
              onBlur={handleUnitsSoldBlur}
            />
          </FieldRow>
        )}
      />
      {!!shouldShowPartialRealisationAllocationLogic && (
        <>
          {!isTotalRealisation && (
            <FieldRow title="Partial realisation allocation logic" centerTitle>
              <Controller
                name={`holdingClassRealisation.${index}.partialRealisationAllocationLogic`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <AppSimpleSelect
                    {...restField}
                    fullWidth
                    options={PARTIAL_REALISATION_ALLOCATION_OPTIONS}
                    value={value}
                    onChange={(event: SelectChangeEvent<unknown>) => {
                      onChange(event.target.value);
                    }}
                  />
                )}
              />
            </FieldRow>
          )}
          <Grid container spacing={{ xs: 0, md: 6 }}>
            <Grid size={{ md: 5, xs: 12 }} />
            <Grid size={{ md: 7, xs: 12 }}>
              <UnitToInvestmentAllocationTable
                control={control}
                index={index}
                unitsAllocationPerInvestment={unitsAllocationPerInvestment}
                onManualAllocationChange={onManualAllocationChange}
                clearConsiderationForm={clearConsiderationForm}
              />
            </Grid>
          </Grid>
        </>
      )}
      <HoldingClassActions
        isFirst={isFirst}
        isLast={isLast}
        hasMoreOptions={holdingClassOptions.length > 1}
        onAddHoldingClass={onAddHoldingClass}
        onRemoveHoldingClass={onRemoveHoldingClass}
      />
    </>
  );
};

export default HoldingClassRealisation;
